// Assumption memo export — PSD §8.6 (F8.6). State layer.

import type { ValuationResult } from '@backsolve/shared-types';

/**
 * Build a plain-text assumption memo from a valuation result.
 */
export function buildAssumptionMemo(result: ValuationResult): string {
  const i = result.inputs;
  const h = result.headline;
  const lines: string[] = [];

  lines.push('ASSUMPTION MEMO');
  lines.push('===============');
  lines.push('');
  lines.push(`Valuation ID: ${result.valuationId}`);
  lines.push(`Computed At: ${result.computedAt}`);
  lines.push(`Engine Version: ${result.engineVersion}`);
  lines.push('');
  lines.push('1. COMPLIANCE');
  lines.push(`   Valuation Date: ${i.compliance.valuationDate}`);
  lines.push(`   Grant Date: ${i.compliance.grantDate ?? 'n/a'}`);
  lines.push(`   Safe Harbor: ${i.compliance.safeHarbor}`);
  lines.push('');
  lines.push('2. EQUITY VALUE ANCHOR');
  lines.push(`   Source: ${i.equityValue.source}`);
  lines.push(`   Anchor Instrument: ${i.equityValue.anchorInstrumentId ?? 'n/a'}`);
  lines.push(`   Anchor Price/share: ${i.equityValue.anchorPricePerShare ?? 'n/a'}`);
  lines.push(`   Direct Equity Value: ${i.equityValue.directEquityValue ?? 'n/a'}`);
  lines.push(`   Arm's-Length: ${i.equityValue.armLengthFlag}`);
  lines.push(`   Transaction Date: ${i.equityValue.transactionDate ?? 'n/a'}`);
  lines.push('');
  lines.push('3. ALLOCATION ASSUMPTIONS');
  lines.push(`   Method: ${i.assumptions.method}`);
  lines.push(`   Volatility: ${(i.assumptions.volatility * 100).toFixed(1)}%`);
  lines.push(`   Time to Liquidity: ${i.assumptions.timeToLiquidity} years`);
  lines.push(`   Risk-Free Rate: ${(i.assumptions.riskFreeRate * 100).toFixed(2)}%`);
  lines.push(`   Dividend Yield: ${(i.assumptions.dividendYield * 100).toFixed(2)}%`);
  if (i.assumptions.methodRationale) {
    lines.push(`   Rationale: ${i.assumptions.methodRationale}`);
  }
  lines.push('');
  lines.push('4. DISCOUNTS');
  lines.push(`   DLOM Model: ${i.discounts.dlomModel}`);
  lines.push(`   DLOM Percentage: ${(i.discounts.dlomPercentage * 100).toFixed(1)}%`);
  lines.push(`   Minority Discount: ${(i.discounts.minorityDiscount * 100).toFixed(1)}%`);
  lines.push('');
  lines.push('5. SOLVER DIAGNOSTICS');
  lines.push(`   Converged: ${result.solver.converged}`);
  lines.push(`   Iterations: ${result.solver.iterations}`);
  lines.push(`   Residual Error: ${result.solver.residualError}`);
  lines.push(`   Tolerance: ${result.solver.tolerance}`);
  lines.push(`   Bracket: [$${result.solver.lowerBound.toFixed(2)}, $${result.solver.upperBound.toFixed(2)}]`);
  lines.push('');
  lines.push('6. HEADLINE OUTPUTS');
  lines.push(`   Total Equity Value: $${h.totalEquityValue.toFixed(2)}`);
  lines.push(`   Common Value (pre-DLOM): $${h.commonValuePreDlom.toFixed(2)}`);
  lines.push(`   Common Value (post-DLOM): $${h.commonValuePostDlom.toFixed(2)}`);
  lines.push(`   Fully Diluted Shares: ${h.fullyDilutedShares.toLocaleString()}`);
  lines.push(`   Common FMV / share: $${h.commonFmvPerShare.toFixed(4)}`);
  lines.push(`   Recommended Strike Price: $${h.recommendedStrikePrice.toFixed(2)}`);
  lines.push(`   Preferred Price / share: ${h.preferredPricePerShare ? `$${h.preferredPricePerShare.toFixed(4)}` : 'n/a'}`);
  lines.push(`   Reconciled: ${result.reconciled}`);
  lines.push('');
  if (result.validation.length > 0) {
    lines.push('7. VALIDATION ISSUES');
    for (const v of result.validation) {
      lines.push(`   [${v.severity.toUpperCase()}] ${v.code}: ${v.message}`);
    }
    lines.push('');
  }

  return lines.join('\n');
}

/**
 * Trigger a browser download of the assumption memo.
 */
export function downloadAssumptionMemo(result: ValuationResult): void {
  const text = buildAssumptionMemo(result);
  const blob = new Blob([text], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `assumption_memo_${result.valuationId}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}
